import { ThemeProvider } from "@emotion/react";
import styled from "@emotion/styled";
import * as React from "react";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import { Dispatch } from "redux";

import { Check, Edit, Group } from "@livechat/design-system-icons";
import { DesignToken, Icon, ModalPortal, SpacingToken } from "@livechat/design-system-react-components";

import { EmptyState } from "components/empty-state/EmptyState";
import { BackButton, Grid, ViewContainer, ViewStack } from "components/grid";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { StyledLink } from "components/links/StyledLink";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import { Right } from "constants/right";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { setDocumentTitle } from "helpers/routing";
import { accountIsCanceled, isExpired } from "helpers/subscription";
import { filterTeam, sortTeams } from "helpers/team";
import { IWithBreakpointsProps, withBreakpoints } from "helpers/withBreakpoints";
import { IStoreState } from "interfaces/storeState";
import { AddOrConfirmModal } from "routes/agents/AddOrConfirmModal";
import TopWrapper from "routes/agents/TopWrapper";
import { AgentsRoutes } from "routes/agents/constants";
import { IAgent, selectAgentsAlphabetically } from "store/entities/agents";
import { IBilling, getBilling } from "store/entities/billing";
import { ISubscription, selectCurrentSubscription } from "store/entities/subscriptions";
import { ITeam, selectTeamsAlphabetically } from "store/entities/teams";
import { hasRight } from "store/features/session";
import { getAreAgentsSaved } from "store/views/agents";
import {
  ISort,
  TeamsViewActions,
  getFocusedColumn,
  getListSorting,
  getSelectedTeamId,
  selectCanAddTeams,
} from "store/views/teams";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";
import { legacyTheme } from "themes/legacyTheme";

import { TeamDetails } from "./TeamDetails";
import { TeamsList } from "./TeamsList";

interface IStateProps {
  agents: IAgent[];
  teams: ITeam[];
  subscription: ISubscription;
  billing: IBilling | null;
  selectedTeamId: string;
  focusedColumn: number;
  areAgentsSaved: boolean;
  canManageAgents: boolean;
  canAddTeams: boolean;
  canManageTeams: boolean;
  sort: ISort;
}

interface IDispatchProps {
  onSort(field: string, desc: boolean): void;
  onSelectTeam(teamId: string): void;
  onSetFocusedColumn(focusedColumn: number): void;
  onBackToList(): void;
}

interface IProps extends IStateProps, IDispatchProps, IWithBreakpointsProps, RouteComponentProps {
  onEditTeam(teamId: string): void;
  onCreateTeam(): void;
  onUpgrade(): void;
  onAgentsTabClicked(): void;
  createMany(): void;
  getContactInfo(email: string): Promise<unknown>;
}

interface IState {
  filterQuery: string;
  isAddDropdownVisible: boolean;
  addAgentsModalVisible: boolean;
  addAgentsSuccessModal: boolean;
}

const StyledContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const TeamsListWrapper = styled.div`
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

function getFilteredTeams(teams: ITeam[], sort: ISort, filterQuery: string): ITeam[] {
  const { field, desc } = sort;
  let filteredTeams = [...teams];

  if (filterQuery) {
    filteredTeams = filteredTeams.filter(filterTeam(filterQuery));
  }
  if (field) {
    filteredTeams = filteredTeams.slice().sort(sortTeams(field, desc));
  }
  return filteredTeams;
}

function isCorrectTeamId(teamId: string, teams: ITeam[]): boolean {
  return teams.some(team => team.id === teamId);
}

class TeamsComponent extends React.PureComponent<IProps, IState> {
  state = {
    filterQuery: "",
    addAgentsModalVisible: false,
    addAgentsSuccessModal: false,
    isAddDropdownVisible: false,
  };

  componentDidMount(): void {
    trackEngagement("Teams: Page opened", EventPlace.Teams);
    this.updateTeamRoute();
  }

  componentDidUpdate(prevProps: IProps): void {
    const { teams, selectedTeamId, match } = this.props;
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const routeParams: any = match.params;

    const teamId = routeParams.id;
    if (teamId !== selectedTeamId || !isCorrectTeamId(selectedTeamId, teams)) {
      this.updateTeamRoute();
      return;
    }

    if (!prevProps.areAgentsSaved && this.props.areAgentsSaved) {
      this.handleToggleSuccessModal();
    }

    setDocumentTitle("Teams");
  }

  handleSetSelectedTeam = (team: ITeam): void => {
    this.props.onSelectTeam(team.id);
    this.props.onSetFocusedColumn(1);
    this.props.history.push(`/agents/teams/${team.id}`);
  };

  handleDoubleClickTeam = (team: ITeam): void => {
    const { canManageTeams } = this.props;

    if (canManageTeams) {
      this.props.history.push(`/agents/teams/${team.id}/edit`);
    }
  };

  handleFilterTeams = (filterQuery: string): void => {
    this.setState({ filterQuery });
  };

  handleBackToList = (): void => {
    this.props.onBackToList();
  };

  forceNavigationToTeam(teamId: string): void {
    this.props.history.replace(`/agents/teams/${teamId}`);
  }

  updateTeamRoute(): void {
    const { teams, sort, selectedTeamId } = this.props;
    const { filterQuery } = this.state;

    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const routeParams: any = this.props.match.params;
    let teamId = routeParams.id || selectedTeamId;
    if (!isCorrectTeamId(teamId, teams)) {
      const filteredTeams = getFilteredTeams(teams, sort, filterQuery);
      teamId = filteredTeams[0].id;
    }

    this.props.onSelectTeam(teamId);
    this.forceNavigationToTeam(teamId);
  }

  handleToggleAddAgentsModal = (): void => {
    this.setState(prevState => ({
      addAgentsModalVisible: !prevState.addAgentsModalVisible,
      addAgentsSuccessModal: false,
    }));
  };

  handleToggleSuccessModal = (): void => {
    this.setState(prevState => ({
      addAgentsModalVisible: false,
      addAgentsSuccessModal: !prevState.addAgentsSuccessModal,
    }));
  };

  handleAddAgentsManually = (eventName: string): void => {
    trackEngagement(eventName, "Agents");
    this.handleToggleAddAgentsModal();
  };

  handleAddDropdownToggle = (): void => {
    this.setState(prevState => ({
      isAddDropdownVisible: !prevState.isAddDropdownVisible,
    }));
  };

  handleSelectAddAgent = (): void => {
    this.setState(prevState => ({
      addAgentsModalVisible: !prevState.addAgentsModalVisible,
      addAgentsSuccessModal: false,
      isAddDropdownVisible: false,
    }));
  };

  handleAddAgentSelect = (): void => {
    trackEngagement("Agents: Invite agents clicked", EventPlace.Agents, { source: "top-bar" });
    this.handleSelectAddAgent();
  };

  render(): React.ReactNode {
    const {
      agents,
      teams,
      subscription,
      billing,
      selectedTeamId,
      focusedColumn,
      areAgentsSaved,
      canManageAgents,
      canAddTeams,
      canManageTeams,
      sort,
      isMobile,
      onSort,
    } = this.props;

    const { filterQuery, isAddDropdownVisible, addAgentsSuccessModal, addAgentsModalVisible } = this.state;
    const filteredTeams = getFilteredTeams(teams, sort, filterQuery);
    const selectedTeam = teams.find(team => team.id === selectedTeamId);
    const subscriptionExpired = accountIsCanceled(billing) || isExpired(subscription);
    const backButton = <BackButton onClick={this.handleBackToList}>Back</BackButton>;

    const editButton = (
      <Block marginRight={withVar(SpacingToken.Spacing2)}>
        <StyledLink
          kind="plain"
          disabled={!canManageTeams || !selectedTeamId}
          to={`/agents/teams/${selectedTeamId}/edit`}
          size="compact"
          icon={<Icon source={Edit} />}
        />
      </Block>
    );

    return (
      <ThemeProvider theme={legacyTheme}>
        <Grid relativeWidths={[3, 1]} minWidths={[320, 320]} focusedColumn={focusedColumn}>
          <ViewStack>
            <StyledContainer>
              {/* @ts-expect-error TS(2741): Property 'isOwner' is missing in type '{ selectedT... Remove this comment to see the full error message */}
              <TopWrapper
                selectedTab="teams"
                agentsCount={agents.length}
                teamsCount={filteredTeams.length}
                filterQuery={filterQuery}
                areAgentsSaved={areAgentsSaved}
                canAddAgent={!subscriptionExpired && canManageAgents}
                canAddTeam={canAddTeams}
                isMobile={isMobile}
                onFilterQueryChange={this.handleFilterTeams}
                isDropdownVisible={isAddDropdownVisible}
                onAddAgentSelect={this.handleAddAgentSelect}
                onDropdownToggle={this.handleAddDropdownToggle}
              />

              <TeamsListWrapper>
                <TeamsList
                  teams={filteredTeams}
                  filterQuery={filterQuery}
                  sort={sort}
                  // @ts-expect-error TS(2322)
                  selectedTeam={selectedTeam}
                  canAddTeam={canManageTeams}
                  onCreateTeam={() => {}}
                  onSelectTeam={this.handleSetSelectedTeam}
                  onDoubleClickTeam={this.handleDoubleClickTeam}
                  onSortTeams={onSort}
                />
                {!filterQuery && teams.length === 1 && canAddTeams && (
                  <Flex justifyContent="center">
                    <EmptyState
                      icon={
                        <Icon source={Group} size="xxlarge" customColor={withVar(DesignToken.ContentBasicPrimary)} />
                      }
                      title="Create more teams"
                      actions={
                        <StyledLink
                          kind="primary"
                          size="large"
                          to={AgentsRoutes.AddTeam}
                          onClick={() =>
                            trackEngagement("Teams: Create team clicked", EventPlace.Teams, { source: "empty-state" })
                          }
                        >
                          Create team
                        </StyledLink>
                      }
                    >
                      Organize your customer support agents into specialized teams and make it easier to share
                      responsibilities.
                    </EmptyState>
                  </Flex>
                )}
              </TeamsListWrapper>
            </StyledContainer>
          </ViewStack>
          <ViewStack>
            <ViewContainer
              title={selectedTeam ? selectedTeam.name : "Details"}
              backButton={backButton}
              rightNodes={editButton}
            >
              {selectedTeam && <TeamDetails agents={agents} team={selectedTeam} />}
            </ViewContainer>
          </ViewStack>
        </Grid>
        {addAgentsModalVisible && (
          <ModalPortal zIndex={StackingContextLevel.Modal}>
            <AddOrConfirmModal onClose={this.handleToggleAddAgentsModal} />
          </ModalPortal>
        )}

        {addAgentsSuccessModal && (
          <ModalPortal zIndex={StackingContextLevel.Modal}>
            <ConfirmActionModal
              icon={Check}
              iconColor={withVar(DesignToken.SurfaceAccentEmphasisHighPositive)}
              onClose={this.handleToggleSuccessModal}
              heading="Your invitations have been sent!"
              secondaryAction={{
                onClick: () => this.handleAddAgentsManually("Agents: Invite more people button clicked"),
                label: "Invite more agents",
              }}
              primaryAction={{
                onClick: this.handleToggleSuccessModal,
                label: "Done",
                actionType: "primary",
                dataTestId: "modal-invite-agents-done-button",
              }}
            >
              Your teammates will receive invitation emails.
            </ConfirmActionModal>
          </ModalPortal>
        )}
      </ThemeProvider>
    );
  }
}

const mapStateToProps = (state: IStoreState): IStateProps => ({
  agents: selectAgentsAlphabetically(state),
  teams: selectTeamsAlphabetically(state),
  subscription: selectCurrentSubscription(state),
  billing: getBilling(state),
  selectedTeamId: getSelectedTeamId(state),
  focusedColumn: getFocusedColumn(state),
  areAgentsSaved: getAreAgentsSaved(state),
  canManageAgents: hasRight(state, Right.ManageAgents),
  canAddTeams: selectCanAddTeams(state),
  canManageTeams: hasRight(state, Right.ManageTeams),
  sort: getListSorting(state),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  // @ts-expect-error TS(2345)
  onSort: (field: string, desc: boolean) => dispatch(TeamsViewActions.setListSorting({ field, desc })),
  // @ts-expect-error TS(2345)
  onSelectTeam: (selectedTeamId: string) => dispatch(TeamsViewActions.setSelectedTeam({ selectedTeamId })),
  // @ts-expect-error TS(2345)
  onSetFocusedColumn: (focusedColumn: number) => dispatch(TeamsViewActions.setFocusedColumn(focusedColumn)),
  // @ts-expect-error TS(2345)
  onBackToList: () => dispatch(TeamsViewActions.setFocusedColumn(0)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withBreakpoints(TeamsComponent)));
